
import React, { useEffect, useState } from 'react';
import 'ag-grid-enterprise';
import axios from 'axios';
import trim from 'lodash/trim';
import { Controller, useForm } from 'react-hook-form';
import { PartnerTypeAndPlatform } from '../common/partnerTypeAndPlatform';
import './partnerOrgDetails.scss';
import {
  SdfAlert,
  SdfBox,
  SdfBusyIndicator,
  SdfButton,
  SdfFocusPane,
  SdfIcon,
  SdfInput,
  SdfSelect,
  SdfSpotIllustration,
} from '@synerg/react-components';
import { get, uniqueId } from 'lodash';
import Toast from '../common/toast';


export default function AddExisitingPartnerModal({ setShowAddExistingPartner, legacyOrg, setLegacyOrg, setAddedExistingPartner, setSearchedOOID }) {

  const { control, handleSubmit, setError, setValue, formState: { errors } } = useForm();
  const errorsMap = {
    invalid: 'Invalid Partner. Please try another OOID.',
    imported: 'Partner already imported. Please try another OOID.',
    not_found: 'No partner found. Please try another OOID.',
    valid: 'Partner found and is available to be added...',
    error: 'Oops! Something went wrong. Please try again.',
  };

  const [allBusinessAdvisors, setAllBusinessAdvisors] = useState([]);
  const [allTechnicalAdvisors, setAllTechnicalAdvisors] = useState([]);
  const [allPartnerAdvisors, setAllPartnerAdvisors] = useState([]);
  const [businessAdvisors, setBusinessAdvisors] = useState([]);
  const [technicalAdvisors, setTechnicalAdvisors] = useState([]);
  const [partnerAdvisors, setPartnerAdvisors] = useState([]);
  const [advisors, setAdvisors] = useState({ technical: [], business: [], partner: [] });
  const [isLoadingPartner, setLoadingPartner] = useState(false);
  const [isImportingPartner, setIsImportingPartner] = useState(false);
  const [assignAdvisorsChoice, setAssignAdvisorsChoice] = useState(null);
  const [toast, setToast] = useState(false);
  const legacyOrgEmpty = Object.keys(legacyOrg).length === 0;


  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true
    });
  };

  const getAdvisorName = (contacts, type) => {
    const advisor = contacts.find((advisor) => advisor.position === type);
    if (advisor) {
      return `${advisor.firstName} ${advisor.lastName}`;
    }
    return;
  }

  const getPartner = async (legacyPartnerOrganization) => {
    try {
      setSearchedOOID(legacyPartnerOrganization.partnerOOID);
      setLoadingPartner(true);
      setLegacyOrg({});
      const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${legacyPartnerOrganization.partnerOOID}/legacy`);
      const { valid } = errorsMap;

      if (![valid].includes(errorsMap[data.status])) {
        setError('partnerOOID', { type: data.status, message: errorsMap[data.status] });
      }
      if (data.orgName) {
        setLegacyOrg(data);
      }
    } catch (error) {
      setError('partnerOOID', { type: 'invalid', message: errorsMap[get(error, 'response.data.status', 'error')] });

    } finally {
      setLoadingPartner(false);
      setAssignAdvisorsChoice(null);
    }
  };

  const getAdvisorId = (advisor, type) => {
    return advisors[type].find(({ fullName }) => fullName === advisor)._id;
  };

  const getAdvisors = async () => {
    try {
      const { data: { technical, business, partner } } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/advisors`);
      setAdvisors({ technical, business, partner });
    } catch (error) {
      const errorMessage = get(error, 'response.data.message', 'Error getting Partner Advisors.');
      handleResponse(errorMessage)
    }
  };

  const importPartner = async (fields) => {
    try {
      setIsImportingPartner(true);
      let partnerAdvisor = fields.partnerAdvisor;
      let businessAdvisor = fields.businessAdvisor;
      let techAdvisor = fields.technicalAdvisor;

      if (partnerAdvisor) {
        partnerAdvisor = getAdvisorId(partnerAdvisor, 'partner');
      }

      if (businessAdvisor) {
        businessAdvisor = getAdvisorId(businessAdvisor, 'business');
      }

      if (techAdvisor) {
        techAdvisor = getAdvisorId(techAdvisor, 'technical');
      }

      const { data } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/partner/legacy/import`, {
        ooid: fields.partnerOOID,
        partnerAdvisor: partnerAdvisor,
        businessAdvisor: businessAdvisor,
        techAdvisor: techAdvisor,
        partnerType: legacyOrg.partnerType
      });

      if (errorsMap[data.status] !== errorsMap.valid) {
        setError('partnerOOID', { type: 'invalid', message: errorsMap[data.status] });
        handleResponse(errorsMap[data.status]);
        if (data.orgName) {
          setLegacyOrg(data);
        }
      } else {
        setLegacyOrg(data);
        setAddedExistingPartner(true);
        setShowAddExistingPartner(false);
      }
      setIsImportingPartner(false);

    } catch (error) {
      const status = get(error, 'response.data.status', 'error');
      setError('partnerOOID', { type: 'invalid', message: errorsMap[status] });
      handleResponse(errorsMap[status]);
      setIsImportingPartner(false);
    }
  };

  const isPartnerOrganizationValid = value => {
    if (trim(value).length === 0) {
      return 'Partner OOID cannot be empty.';
    }
    if ((/[^A-Za-z0-9-., ]/.test(trim(value)))) {
      return 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), comma (,) and spaces';
    }
    return true;
  };

  const handleFilter = (e, type) => {
    switch (type) {
      case "business":
        setBusinessAdvisors(
          allBusinessAdvisors.filter(partner =>
            partner.value.toLowerCase().includes(e.detail.toLowerCase())
          )
        );
        break;
      case "partner":
        setPartnerAdvisors(
          allPartnerAdvisors.filter(partner =>
            partner.value.toLowerCase().includes(e.detail.toLowerCase())
          )
        );
        break;
      default:
        setTechnicalAdvisors(
          allTechnicalAdvisors.filter(partner =>
            partner.value.toLowerCase().includes(e.detail.toLowerCase())
          )
        );
        break;
    }
  };


  useEffect(() => {
    getAdvisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAddedExistingPartner(false);
    setLegacyOrg({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getAdvisorNames = type => advisors[type].map(({ fullName }) => ({ label: fullName, value: fullName }));

    setAllTechnicalAdvisors(getAdvisorNames('technical'));
    setAllBusinessAdvisors(getAdvisorNames('business'));
    setAllPartnerAdvisors(getAdvisorNames('partner'));
    setTechnicalAdvisors(getAdvisorNames('technical'));
    setBusinessAdvisors(getAdvisorNames('business'));
    setPartnerAdvisors(getAdvisorNames('partner'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisors]);

  useEffect(() => {
    if (Object.keys(legacyOrg).length > 0 && legacyOrg.contacts) {
      setValue('businessAdvisor', getAdvisorName(legacyOrg.contacts, 'business'));
      setValue('technicalAdvisor', getAdvisorName(legacyOrg.contacts, 'technical'));
      setValue('partnerAdvisor', getAdvisorName(legacyOrg.contacts, 'partner'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyOrg]);


  const ShowError = ({ message, alertStatus = "error" }) => {
    return (
      <SdfAlert
        slot="alert"
        size="sm"
        status={alertStatus}
        type="inline"
      >
        {message}
      </SdfAlert>
    );
  };



  return (
    <SdfFocusPane
      paneType="floating"
      visible={true}
      size="small"
      onSdfDismiss={() => {
        setShowAddExistingPartner(false);
      }}
      closeable={!isImportingPartner}
      hideAcceptButton={true}
      hideDismissButton={true}
    >

      {toast && <Toast
        key={toast.key}
        status={toast.status}
        message={toast.message}
        showToast={toast.showToast}
      />}

      <div className="px-6">
        <div className="text-heading-05 font-bold" >Search by partner OOID</div>
        <div className='text-body-01'>Add an existing partner by first searching their OOID below.</div>
        <div className="mt-5 flex justify-between items-start">
          <Controller
            name="partnerOOID"
            control={control}
            rules={{
              validate: isPartnerOrganizationValid
            }}
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <>
                <SdfInput
                  name="partnerOOID"
                  className="w-3/4"
                  placeholder="Search by partner OOID"
                  onSdfInput={({ detail }) => {
                    onChange(detail);
                    !legacyOrgEmpty && setLegacyOrg({});
                  }}
                  value={value}
                >
                  <SdfIcon slot="after" icon="action-search" />
                  {error && error.message && <ShowError message={"Invalid partner OOID or partner not found. Please try with a different or a valid OOID."} />}
                  {!(error) && legacyOrg.status === 'valid' && <ShowError message={errorsMap.valid} alertStatus="success" />}
                </SdfInput>
              </>
            )}
          />

          <SdfButton
            type="submit"
            emphasis="primary"
            onClick={handleSubmit((data) => getPartner(data))}
            {...(isLoadingPartner || isImportingPartner) && { disabled: true }}
            style={{ marginTop: "0.45rem" }}
          >
            <div style={{ height: '1.625rem' }}>
              Check
              {isLoadingPartner && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
            </div>
          </SdfButton>
        </div>

        {legacyOrgEmpty ? <div className="flex flex-col items-center mt-10 mb-8">
          <SdfSpotIllustration
            illustrationName={'empty-magnifying-glass'}
            size={'md'}
          ></SdfSpotIllustration>

          <div className="text-body-02 font-medium empty-search">No results found yet</div>
          <div className="text-body-01 empty-search">Check partner OOID to view partner name</div>

        </div> :
          <div>
            <div className="mt-6">
              <div className="text-heading-02 font-bold">{legacyOrg.orgName}</div>
              <div className="text-body-01 font-medium">Partner name</div>
            </div>

            <div className="mt-4">
              <div className="text-heading-02 font-bold">{PartnerTypeAndPlatform[legacyOrg.partnerType]}</div>
              <div className="text-body-01 font-medium">Organization type</div>
            </div>

            <div className="mt-4">
              <div className="text-heading-02 font-bold">{legacyOrg.isiOrgCd || 'NA'}</div>
              <div className="text-body-01 font-medium">Partner ID</div>
            </div>

            {legacyOrg.status === 'valid' && !(assignAdvisorsChoice === "Yes") && <SdfBox variant="filled" className="mt-4">
              {assignAdvisorsChoice === null && <div className="flex justify-between items-start">
                <div className="text-body-02">Do you want to assign advisors now?</div>
                <div className="flex">
                  <SdfButton emphasis="tertiary" className="mr-2" onClick={() => setAssignAdvisorsChoice("No")}>No</SdfButton>
                  <SdfButton emphasis="secondary" onClick={() => setAssignAdvisorsChoice("Yes")}>Yes</SdfButton>
                </div>
              </div>}
              {assignAdvisorsChoice === "No" && <div className="text-body-02 font-medium" style={{ letterSpacing: "-0.29px" }}>That’s okay! You’ll have a chance to add advisors later.</div>}
            </SdfBox>}

            {assignAdvisorsChoice === "Yes" && <SdfBox
              shape="rounded"
              variant="filled"
              spacing="loose"
              className="mt-6 mb-2"
            >
              <div className="text-heading-03 font-bold ">Assign advisors</div>
              <Controller
                name="businessAdvisor"
                control={control}
                render={({
                  field: { value, onChange }
                }) => (
                  <>
                    <SdfSelect
                      label={"Business development advisor"}
                      className="mt-4 w-full"
                      filterable={true}
                      items={businessAdvisors}
                      value={{ label: value || 'Not Assigned', value: value || 'Not Assigned' }}
                      placeholder="Select one..."
                      onSdfChange={({ detail }) => { onChange(detail.value); setBusinessAdvisors(allBusinessAdvisors); }}
                      onSdfFilter={(e) => { handleFilter(e, 'business') }}
                    />
                  </>
                )}
              />

              <Controller
                name="technicalAdvisor"
                control={control}
                render={({
                  field: { value, onChange }
                }) => (
                  <>
                    <SdfSelect
                      label={"Technical advisor"}
                      className="mt-4 w-full"
                      filterable={true}
                      items={technicalAdvisors}
                      value={{ label: value || 'Not Assigned', value: value || 'Not Assigned' }}
                      placeholder="Select one..."
                      onSdfChange={({ detail }) => { onChange(detail.value); setTechnicalAdvisors(allTechnicalAdvisors); }}
                      onSdfFilter={(e) => { handleFilter(e, 'technical') }}
                    />
                  </>
                )}
              />

              <Controller
                name="partnerAdvisor"
                control={control}
                render={({
                  field: { value, onChange }
                }) => (
                  <>
                    <SdfSelect
                      label={"Partner development advisor"}
                      className="mt-4 w-full"
                      filterable={true}
                      items={partnerAdvisors}
                      value={{ label: value || 'Not Assigned', value: value || 'Not Assigned' }}
                      placeholder="Select one..."
                      onSdfChange={({ detail }) => { onChange(detail.value); setPartnerAdvisors(allPartnerAdvisors); }}
                      onSdfFilter={(e) => { handleFilter(e, 'partner') }}
                    />
                  </>
                )}
              />
            </SdfBox>}
          </div>
        }
      </div>

      <div slot={"custom-buttons"} className="mt-3">
        <SdfButton
          onClick={() => { setShowAddExistingPartner(false) }}
          {...(isImportingPartner || isLoadingPartner) && { disabled: true }}
        >
          Cancel
        </SdfButton>

        <SdfButton
          emphasis="primary"
          onClick={handleSubmit(importPartner)}
          {...(isImportingPartner || legacyOrgEmpty || Object.keys(errors).length > 0 || assignAdvisorsChoice === null) && { disabled: true }}
          className="ml-3"
        >
          <div style={{ height: '1.625rem' }}>
            Add partner
            {isImportingPartner && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
          </div>
        </SdfButton>
      </div>
    </SdfFocusPane >
  )
}
